<template>
  <div class="results-container">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-hand-holding-dollar"></i>
        Registros de Cobrança
        <p class="page-subtitle">
          Histórico de cobranças: saldo anterior, valor recebido e saldo final
        </p>
      </h1>
    </div>

    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <div v-show="!loading" class="tab-content">
      <div class="filters-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-filter"></i>
            Filtros de Consulta
          </h3>
        </div>

        <div class="filters-content">
          <div class="filters-grid">
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-tie"></i>
                Cobrador
              </label>
              <div class="searchable-select-wrapper">
                <div class="searchable-select">
                  <input
                    type="text"
                    class="form-input searchable-select-input"
                    autocomplete="off"
                    spellcheck="false"
                    placeholder="Digite para buscar cobrador..."
                    v-model="collectorSearch"
                    :disabled="isCobrador"
                    @focus="openCollectorDropdown"
                    @blur="onCollectorBlur"
                    @input="onCollectorInput"
                  />
                  <span class="searchable-select-arrow">▼</span>
                  <div
                    v-if="showCollectorDropdown"
                    class="searchable-select-dropdown"
                  >
                    <div
                      v-if="!isCobrador"
                      class="searchable-select-option"
                      :class="{ 'is-selected': !filters.collectorId }"
                      @mousedown.prevent="selectCollectorAll"
                    >
                      Todos os cobradores
                    </div>
                    <div
                      v-for="c in filteredCollectors"
                      :key="c.id"
                      class="searchable-select-option"
                      :class="{ 'is-selected': String(filters.collectorId) === String(c.id) }"
                      @mousedown.prevent="selectCollector(c)"
                    >
                      {{ c.name }}
                    </div>
                    <div
                      v-if="filteredCollectors.length === 0"
                      class="searchable-select-option no-results"
                    >
                      Nenhum cobrador encontrado
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user"></i>
                Vendedor
              </label>
              <div class="searchable-select-wrapper">
                <div class="searchable-select">
                  <input
                    type="text"
                    class="form-input searchable-select-input"
                    autocomplete="off"
                    spellcheck="false"
                    placeholder="Digite para buscar vendedor..."
                    v-model="sellerSearch"
                    @focus="openSellerDropdown"
                    @blur="onSellerBlur"
                    @input="onSellerInput"
                  />
                  <span class="searchable-select-arrow">▼</span>
                  <div
                    v-if="showSellerDropdown"
                    class="searchable-select-dropdown"
                  >
                    <div
                      class="searchable-select-option"
                      :class="{ 'is-selected': !filters.sellerId }"
                      @mousedown.prevent="selectSellerAll"
                    >
                      Todos os vendedores
                    </div>
                    <div
                      v-for="s in filteredSellers"
                      :key="s.id"
                      class="searchable-select-option"
                      :class="{ 'is-selected': String(filters.sellerId) === String(s.id) }"
                      @mousedown.prevent="selectSeller(s)"
                    >
                      {{ s.name }}
                    </div>
                    <div
                      v-if="filteredSellers.length === 0"
                      class="searchable-select-option no-results"
                    >
                      Nenhum vendedor encontrado
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-calendar-days"></i>
                Data Início *
              </label>
              <input
                class="form-input"
                type="date"
                v-model="filters.dateBegin"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-calendar-days"></i>
                Data Fim *
              </label>
              <input
                class="form-input"
                type="date"
                v-model="filters.dateEnd"
              />
            </div>
          </div>

          <div class="filters-actions">
            <button type="button" class="btn-primary" @click="search(1)">
              <i class="fa-solid fa-search"></i>
              Pesquisar
            </button>
            <button type="button" class="btn-secondary" @click="clearFilters()">
              <i class="fa-solid fa-eraser"></i>
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      <!-- Cards de resumo (respeitam filtros) -->
      <div class="charges-summary-grid">
        <div class="charges-summary-card card-charges">
          <div class="summary-icon"><i class="fa-solid fa-receipt"></i></div>
          <div class="summary-body">
            <span class="summary-label">Cobranças</span>
            <span class="summary-value">{{ summary.totalCharges }}</span>
          </div>
        </div>
        <div class="charges-summary-card card-received">
          <div class="summary-icon"><i class="fa-solid fa-money-bill-wave"></i></div>
          <div class="summary-body">
            <span class="summary-label">Valor recebido</span>
            <span class="summary-value">{{ formatMoney(summary.totalAmountReceived) }}</span>
          </div>
        </div>
        <div class="charges-summary-card card-open">
          <div class="summary-icon"><i class="fa-solid fa-scale-balanced"></i></div>
          <div class="summary-body">
            <span class="summary-label">Saldo final (soma)</span>
            <span class="summary-value">{{ formatMoney(summary.totalAmountOpenAfter) }}</span>
          </div>
        </div>
        <div
          class="charges-summary-card card-attended is-clickable"
          title="Exportar vendedores com cobrança no período"
          @click="exportSellersCoverage(true, 'excel')"
        >
          <div class="summary-icon"><i class="fa-solid fa-user-check"></i></div>
          <div class="summary-body">
            <span class="summary-label">Vendedores atendidos</span>
            <span class="summary-value">{{ summary.sellersAttendedCount }}</span>
            <span class="summary-hint">de {{ summary.eligibleSellersCount }} elegíveis · clique p/ exportar</span>
          </div>
        </div>
        <div
          class="charges-summary-card card-pending is-clickable"
          title="Exportar vendedores sem cobrança no período"
          @click="exportSellersCoverage(false, 'excel')"
        >
          <div class="summary-icon"><i class="fa-solid fa-user-xmark"></i></div>
          <div class="summary-body">
            <span class="summary-label">Sem cobrança</span>
            <span class="summary-value">{{ summary.sellersWithoutChargeCount }}</span>
            <span class="summary-hint">no período · clique p/ exportar</span>
          </div>
        </div>
      </div>

      <div v-show="rows && rows.length > 0" class="results-card">
        <div class="table-header charges-table-header">
          <h3 class="card-title">
            <i class="fa-solid fa-table"></i>
            Cobranças Encontradas
            <span class="table-count">({{ totalRows }} registro(s) encontrado(s))</span>
          </h3>

          <div class="card-actions">
            <div class="table-zoom-controls" title="Zoom da tabela">
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom <= 5"
                @click="decreaseTableZoom"
                title="Diminuir zoom"
              >
                <i class="fa-solid fa-magnifying-glass-minus"></i>
              </button>
              <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
                {{ tableZoom }}%
              </span>
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom >= 100"
                @click="increaseTableZoom"
                title="Aumentar zoom"
              >
                <i class="fa-solid fa-magnifying-glass-plus"></i>
              </button>
            </div>

            <div class="columns-dropdown" ref="columnsDropdown">
              <button
                type="button"
                class="btn-action btn-columns"
                @click.stop="showColumnsMenu = !showColumnsMenu; if (showColumnsMenu) showSellersExportMenu = false"
                title="Mostrar ou esconder colunas"
              >
                <i class="fa-solid fa-columns"></i>
                Colunas
                <i class="fa-solid fa-caret-down columns-caret"></i>
              </button>
              <div v-if="showColumnsMenu" class="columns-menu" @click.stop>
                <div class="columns-menu-header">
                  <span>Colunas visíveis</span>
                  <button type="button" class="columns-reset" @click="resetVisibleColumns">
                    Restaurar
                  </button>
                </div>
                <label
                  v-for="col in toggleableColumns"
                  :key="col.key"
                  class="columns-menu-item"
                  :class="{ locked: col.locked }"
                >
                  <input
                    type="checkbox"
                    :checked="isColumnVisible(col.key)"
                    :disabled="col.locked"
                    @change="toggleColumn(col.key)"
                  />
                  <span>{{ col.label }}</span>
                </label>
              </div>
            </div>

            <button
              type="button"
              class="btn-action btn-print"
              @click="printReport()"
              title="Imprimir relatório"
            >
              <i class="fa-solid fa-print"></i>
              Imprimir
            </button>
            <button
              type="button"
              class="btn-action btn-pdf"
              @click="exportToPDF()"
              title="Exportar para PDF"
            >
              <i class="fa-solid fa-file-pdf"></i>
              PDF
            </button>
            <button
              type="button"
              class="btn-action btn-excel"
              @click="exportToExcel()"
              title="Exportar cobranças para Excel"
            >
              <i class="fa-solid fa-file-excel"></i>
              Excel
            </button>

            <div class="columns-dropdown sellers-export-dropdown" ref="sellersExportDropdown">
              <button
                type="button"
                class="btn-action btn-sellers-export"
                @click.stop="toggleSellersExportMenu"
                title="Exportar vendedores com ou sem cobrança"
              >
                <i class="fa-solid fa-users"></i>
                Vendedores
                <i class="fa-solid fa-caret-down columns-caret"></i>
              </button>
              <div v-if="showSellersExportMenu" class="columns-menu sellers-export-menu" @click.stop>
                <div class="columns-menu-header">
                  <span>Exportar por cobertura</span>
                </div>
                <button
                  type="button"
                  class="sellers-export-item"
                  @click="exportSellersCoverage(true, 'excel')"
                >
                  <i class="fa-solid fa-file-excel"></i>
                  Com cobrança · Excel
                </button>
                <button
                  type="button"
                  class="sellers-export-item"
                  @click="exportSellersCoverage(false, 'excel')"
                >
                  <i class="fa-solid fa-file-excel"></i>
                  Sem cobrança · Excel
                </button>
                <button
                  type="button"
                  class="sellers-export-item"
                  @click="exportSellersCoverage(true, 'pdf')"
                >
                  <i class="fa-solid fa-file-pdf"></i>
                  Com cobrança · PDF
                </button>
                <button
                  type="button"
                  class="sellers-export-item"
                  @click="exportSellersCoverage(false, 'pdf')"
                >
                  <i class="fa-solid fa-file-pdf"></i>
                  Sem cobrança · PDF
                </button>
              </div>
            </div>

            <div v-if="totalPages > 1" class="pagination-info">
              <span>Página {{ pageIndex }} de {{ totalPages }}</span>
              <div class="pagination-controls">
                <button
                  class="btn-pagination"
                  @click="changePage(pageIndex - 1)"
                  :disabled="pageIndex <= 1"
                >
                  <i class="fa-solid fa-chevron-left"></i>
                </button>
                <button
                  class="btn-pagination"
                  @click="changePage(pageIndex + 1)"
                  :disabled="pageIndex >= totalPages"
                >
                  <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="table-container" :style="tableZoomStyle">
          <table class="modern-table charges-table">
            <thead>
              <tr>
                <th v-if="isColumnVisible('chargeDateTime')">Data / Hora</th>
                <th v-if="isColumnVisible('collectorName')">Cobrador</th>
                <th v-if="isColumnVisible('sellerName')">Vendedor</th>
                <th v-if="isColumnVisible('sellerPoint')">Ponto</th>
                <th v-if="isColumnVisible('regionName')">Região</th>
                <th v-if="isColumnVisible('amountDueBefore')">Saldo anterior</th>
                <th v-if="isColumnVisible('amountReceived')">Valor</th>
                <th v-if="isColumnVisible('amountOpenAfter')">Saldo final</th>
                <th v-if="isColumnVisible('description')">Descrição</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="data-row"
              >
                <td v-if="isColumnVisible('chargeDateTime')">
                  <div class="date-cell nowrap-cell">
                    <i class="fa-solid fa-calendar-day"></i>
                    <span>{{ formatDateTime(row.chargeDateTime) }}</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('collectorName')">
                  <div class="seller-cell nowrap-cell">
                    <i class="fa-solid fa-user-tie"></i>
                    <span>{{ row.collectorName || '—' }}</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('sellerName')">
                  <div class="seller-cell nowrap-cell">
                    <i class="fa-solid fa-user"></i>
                    <span>{{ row.sellerName || '—' }}</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('sellerPoint')">
                  <span class="detail-text nowrap-cell">{{ row.sellerPoint || '—' }}</span>
                </td>
                <td v-if="isColumnVisible('regionName')">
                  <div class="region-cell nowrap-cell">
                    <i class="fa-solid fa-map-marker-alt"></i>
                    <span>{{ row.regionName || '—' }}</span>
                  </div>
                </td>
                <td v-if="isColumnVisible('amountDueBefore')">
                  <span class="currency-value nowrap-cell">{{ formatMoney(row.amountDueBefore) }}</span>
                </td>
                <td v-if="isColumnVisible('amountReceived')">
                  <span class="currency-value currency-positive nowrap-cell">{{ formatMoney(row.amountReceived) }}</span>
                </td>
                <td v-if="isColumnVisible('amountOpenAfter')">
                  <span
                    class="currency-value nowrap-cell"
                    :class="balanceValueClass(row.amountOpenAfter)"
                  >
                    {{ formatMoney(row.amountOpenAfter) }}
                  </span>
                </td>
                <td v-if="isColumnVisible('description')">
                  <span class="description-text nowrap-cell">{{ row.description || '—' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-footer">
          <div class="pagination-info">
            <span>Mostrando {{ startItem }}-{{ endItem }} de {{ totalRows }} registros</span>
          </div>
          <div class="pagination-controls">
            <button
              class="btn-pagination"
              @click="changePage(1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="btn-pagination"
              @click="changePage(pageIndex - 1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="page-info">{{ pageIndex }} / {{ totalPages }}</span>
            <button
              class="btn-pagination"
              @click="changePage(pageIndex + 1)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="btn-pagination"
              @click="changePage(totalPages)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <div v-show="!rows || rows.length === 0" class="empty-state">
        <div class="empty-state-content">
          <i class="fa-solid fa-hand-holding-dollar"></i>
          <h3>Nenhum registro de cobrança encontrado</h3>
          <p>Configure o período e clique em pesquisar para visualizar as cobranças</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const COLUMNS_STORAGE_KEY = "viewCollectorCharges.visibleColumns";
const TABLE_ZOOM_STORAGE_KEY = "viewCollectorCharges.tableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

const DEFAULT_VISIBLE_COLUMNS = {
  chargeDateTime: true,
  collectorName: true,
  sellerName: true,
  sellerPoint: true,
  regionName: true,
  amountDueBefore: true,
  amountReceived: true,
  amountOpenAfter: true,
  description: true,
};

function todayYmd() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default {
  name: "ViewCollectorCharges",
  data() {
    return {
      loading: false,
      currentUser: null,
      userResponsibleRegions: [],
      bancas: [],
      regions: [],
      collectors: [],
      sellers: [],
      rows: [],
      pageIndex: 1,
      pageSize: 50,
      totalRows: 0,
      totalPages: 1,
      filters: {
        collectorId: "",
        sellerId: "",
        dateBegin: todayYmd(),
        dateEnd: todayYmd(),
      },
      collectorSearch: "",
      sellerSearch: "",
      showCollectorDropdown: false,
      showSellerDropdown: false,
      showColumnsMenu: false,
      showSellersExportMenu: false,
      exportingSellers: false,
      visibleColumns: { ...DEFAULT_VISIBLE_COLUMNS },
      tableZoom: 100,
      summary: {
        totalCharges: 0,
        totalAmountReceived: 0,
        totalAmountDueBefore: 0,
        totalAmountOpenAfter: 0,
        sellersAttendedCount: 0,
        sellersWithoutChargeCount: 0,
        eligibleSellersCount: 0,
      },
      headerRegion: (() => {
        try {
          const raw = localStorage.getItem("currentRegion");
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })(),
      headerBanca: (() => {
        try {
          const raw = localStorage.getItem("selectedBanca");
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })(),
    };
  },
  computed: {
    currentRegionId() {
      return this.headerRegion?.id || null;
    },
    currentSelectedBanca() {
      return this.headerBanca || null;
    },
    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },
    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },
    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },
    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },
    startItem() {
      if (this.totalRows === 0) return 0;
      return (this.pageIndex - 1) * this.pageSize + 1;
    },
    endItem() {
      return Math.min(this.pageIndex * this.pageSize, this.totalRows);
    },
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },
    filteredCollectors() {
      const q = (this.collectorSearch || "").trim().toLowerCase();
      let list = this.collectors || [];
      if (!q || this.isExactCollectorLabel(q)) {
        return list;
      }
      return list.filter((c) => (c.name || "").toLowerCase().includes(q));
    },
    filteredSellers() {
      const q = (this.sellerSearch || "").trim().toLowerCase();
      let list = this.sellers || [];

      // Se um cobrador estiver selecionado, restringe vendedores vinculados a ele
      if (this.filters.collectorId) {
        list = list.filter(
          (s) =>
            String(s.collectorId || s.CollectorId || "") ===
            String(this.filters.collectorId),
        );
      }

      if (!q || this.isExactSellerLabel(q)) {
        return list;
      }
      return list.filter((s) => (s.name || "").toLowerCase().includes(q));
    },
    toggleableColumns() {
      return [
        { key: "chargeDateTime", label: "Data / Hora", locked: true },
        { key: "collectorName", label: "Cobrador" },
        { key: "sellerName", label: "Vendedor" },
        { key: "sellerPoint", label: "Ponto" },
        { key: "regionName", label: "Região" },
        { key: "amountDueBefore", label: "Saldo anterior" },
        { key: "amountReceived", label: "Valor" },
        { key: "amountOpenAfter", label: "Saldo final" },
        { key: "description", label: "Descrição" },
      ];
    },
  },
  async mounted() {
    this.loadVisibleColumns();
    this.loadTableZoom();
    document.addEventListener("click", this.handleColumnsMenuOutsideClick);
    window.addEventListener("regionChanged", this.onHeaderRegionChanged);
    window.addEventListener("regionAutoSelected", this.onHeaderRegionChanged);
    window.addEventListener("bancaChanged", this.onHeaderBancaChanged);
    window.addEventListener("storage", this.onHeaderFilterStorage);

    try {
      this.currentUser = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      this.currentUser = null;
    }

    if (this.currentUser && !this.isAdmin) {
      await this.loadUserResponsibleRegions();
    }

    await Promise.all([this.loadBancas(), this.loadRegions()]);

    if (this.isBanca) {
      await this.loadBancaRegions();
    }

    await Promise.all([this.loadCollectors(), this.loadSellers()]);
    this.syncFiltersToCurrentRegion();
    await this.search(1);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleColumnsMenuOutsideClick);
    window.removeEventListener("regionChanged", this.onHeaderRegionChanged);
    window.removeEventListener("regionAutoSelected", this.onHeaderRegionChanged);
    window.removeEventListener("bancaChanged", this.onHeaderBancaChanged);
    window.removeEventListener("storage", this.onHeaderFilterStorage);
  },
  methods: {
    onHeaderFilterStorage(e) {
      if (e.key === "currentRegion" || e.key === "selectedBanca") {
        if (e.key === "currentRegion") {
          try {
            this.headerRegion = e.newValue ? JSON.parse(e.newValue) : null;
          } catch {
            this.headerRegion = null;
          }
        }
        if (e.key === "selectedBanca") {
          try {
            this.headerBanca = e.newValue ? JSON.parse(e.newValue) : null;
          } catch {
            this.headerBanca = null;
          }
        }
        this.handleRegionChange();
      }
    },
    onHeaderRegionChanged(event) {
      this.headerRegion = event?.detail?.region ?? this.readRegionFromStorage();
      this.handleRegionChange();
    },
    onHeaderBancaChanged(event) {
      this.headerBanca = event?.detail?.banca ?? this.readBancaFromStorage();
      this.handleRegionChange();
    },
    readRegionFromStorage() {
      try {
        const raw = localStorage.getItem("currentRegion");
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    readBancaFromStorage() {
      try {
        const raw = localStorage.getItem("selectedBanca");
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    },
    async handleRegionChange() {
      this.filters.collectorId = "";
      this.filters.sellerId = "";
      this.collectorSearch = "";
      this.sellerSearch = "";
      await Promise.all([this.loadCollectors(), this.loadSellers()]);
      this.syncFiltersToCurrentRegion();
      await this.search(1);
    },
    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = (response.data || []).map((region) => region.id);
        if (
          this.currentUser.regionId &&
          !this.userResponsibleRegions.some(
            (id) => String(id) === String(this.currentUser.regionId),
          )
        ) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch {
        this.userResponsibleRegions = [];
      }
    },
    async loadBancas() {
      try {
        const response = await mainService.searchBancas({ isActive: true });
        this.bancas = response.data || [];
      } catch {
        this.bancas = [];
      }
    },
    async loadRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
      } catch {
        this.regions = [];
      }
    },
    async loadBancaRegions() {
      try {
        if (!this.currentUser || !this.isBanca) return;
        const bancasForUser = this.bancas.filter(
          (banca) =>
            banca.responsibleUserIds &&
            banca.responsibleUserIds.includes(this.currentUser.id),
        );
        for (const banca of bancasForUser) {
          if (banca.regionIds && Array.isArray(banca.regionIds)) {
            banca.regionIds.forEach((regionId) => {
              if (
                !this.userResponsibleRegions.some(
                  (id) => String(id) === String(regionId),
                )
              ) {
                this.userResponsibleRegions.push(regionId);
              }
            });
          }
        }
      } catch {
        // ignore
      }
    },
    syncFiltersToCurrentRegion() {
      const allowedCollectorIds = new Set(
        (this.collectors || []).map((c) => String(c.id)),
      );
      const allowedSellerIds = new Set(
        (this.sellers || []).map((s) => String(s.id)),
      );

      if (
        this.filters.collectorId &&
        !allowedCollectorIds.has(String(this.filters.collectorId))
      ) {
        this.filters.collectorId = "";
        this.collectorSearch = "";
      }

      if (
        this.filters.sellerId &&
        !allowedSellerIds.has(String(this.filters.sellerId))
      ) {
        this.filters.sellerId = "";
        this.sellerSearch = "";
      }

      if (this.isCobrador && this.collectors.length > 0) {
        const self = this.collectors[0];
        this.filters.collectorId = self.id;
        this.collectorSearch = self.name || "";
      }
    },
    formatMoney(v) {
      return Number(v || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    balanceValueClass(value) {
      const v = Number(value);
      if (v > 0) return "currency-negative";
      if (v < 0) return "currency-positive";
      return "";
    },
    formatDateTime(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    clearFilters() {
      this.filters = {
        collectorId: this.isCobrador ? this.currentUser?.id || "" : "",
        sellerId: "",
        dateBegin: todayYmd(),
        dateEnd: todayYmd(),
      };
      this.collectorSearch = "";
      this.sellerSearch = "";
      this.showCollectorDropdown = false;
      this.showSellerDropdown = false;
      this.syncFiltersToCurrentRegion();
      this.search(1);
    },
    periodDisplayText() {
      return `${this.filters.dateBegin || "—"} até ${this.filters.dateEnd || "—"}`;
    },

    isExactCollectorLabel(q) {
      if (!q) return false;
      if (q === "todos os cobradores") return true;
      if (!this.filters.collectorId) return false;
      const selected = (this.collectors || []).find(
        (c) => String(c.id) === String(this.filters.collectorId),
      );
      return (selected?.name || "").toLowerCase() === q;
    },
    isExactSellerLabel(q) {
      if (!q) return false;
      if (q === "todos os vendedores") return true;
      if (!this.filters.sellerId) return false;
      const selected = (this.sellers || []).find(
        (s) => String(s.id) === String(this.filters.sellerId),
      );
      return (selected?.name || "").toLowerCase() === q;
    },

    openCollectorDropdown() {
      if (this.isCobrador) return;
      this.showSellerDropdown = false;
      this.showCollectorDropdown = true;
    },
    onCollectorBlur() {
      window.setTimeout(() => {
        this.showCollectorDropdown = false;
        this.syncCollectorSearchLabel();
      }, 150);
    },
    onCollectorInput() {
      if (this.isCobrador) return;
      this.showCollectorDropdown = true;
      if (this.filters.collectorId) {
        const selected = (this.collectors || []).find(
          (c) => String(c.id) === String(this.filters.collectorId),
        );
        if (
          !selected ||
          (selected.name || "").toLowerCase() !==
            (this.collectorSearch || "").trim().toLowerCase()
        ) {
          this.filters.collectorId = "";
        }
      }
    },
    selectCollectorAll() {
      if (this.isCobrador) return;
      this.filters.collectorId = "";
      this.collectorSearch = "";
      this.filters.sellerId = "";
      this.sellerSearch = "";
      this.showCollectorDropdown = false;
    },
    selectCollector(collector) {
      if (this.isCobrador) return;
      this.filters.collectorId = collector.id;
      this.collectorSearch = collector.name || "";
      this.filters.sellerId = "";
      this.sellerSearch = "";
      this.showCollectorDropdown = false;
    },
    syncCollectorSearchLabel() {
      if (!this.filters.collectorId) {
        if ((this.collectorSearch || "").trim().toLowerCase() === "todos os cobradores") {
          this.collectorSearch = "";
        }
        return;
      }
      const selected = (this.collectors || []).find(
        (c) => String(c.id) === String(this.filters.collectorId),
      );
      this.collectorSearch = selected?.name || "";
    },

    openSellerDropdown() {
      this.showCollectorDropdown = false;
      this.showSellerDropdown = true;
    },
    onSellerBlur() {
      window.setTimeout(() => {
        this.showSellerDropdown = false;
        this.syncSellerSearchLabel();
      }, 150);
    },
    onSellerInput() {
      this.showSellerDropdown = true;
      if (this.filters.sellerId) {
        const selected = (this.sellers || []).find(
          (s) => String(s.id) === String(this.filters.sellerId),
        );
        if (
          !selected ||
          (selected.name || "").toLowerCase() !==
            (this.sellerSearch || "").trim().toLowerCase()
        ) {
          this.filters.sellerId = "";
        }
      }
    },
    selectSellerAll() {
      this.filters.sellerId = "";
      this.sellerSearch = "";
      this.showSellerDropdown = false;
    },
    selectSeller(seller) {
      this.filters.sellerId = seller.id;
      this.sellerSearch = seller.name || "";
      this.showSellerDropdown = false;
    },
    syncSellerSearchLabel() {
      if (!this.filters.sellerId) {
        if ((this.sellerSearch || "").trim().toLowerCase() === "todos os vendedores") {
          this.sellerSearch = "";
        }
        return;
      }
      const selected = (this.sellers || []).find(
        (s) => String(s.id) === String(this.filters.sellerId),
      );
      this.sellerSearch = selected?.name || "";
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.search(page);
    },
    filterCollectorsByAccess(collectors) {
      let list = [...(collectors || [])];

      if (this.isCobrador) {
        list = list.filter(
          (c) => String(c.id) === String(this.currentUser?.id),
        );
        return list;
      }

      if (!this.isAdmin) {
        list = list.filter((c) =>
          this.userResponsibleRegions.some(
            (id) => String(id) === String(c.regionId || c.RegionId),
          ),
        );
      }

      if (this.currentRegionId) {
        list = list.filter(
          (c) =>
            String(c.regionId || c.RegionId || "") ===
            String(this.currentRegionId),
        );
      } else if (this.isAdmin && this.currentSelectedBanca) {
        const bancaRegionIds = this.regions
          .filter(
            (r) =>
              String(r.bancaId) === String(this.currentSelectedBanca.id),
          )
          .map((r) => String(r.id));
        list = list.filter((c) =>
          bancaRegionIds.includes(String(c.regionId || c.RegionId || "")),
        );
      } else if (!this.isAdmin && this.currentSelectedBanca) {
        list = list.filter((c) => {
          const region = this.regions.find(
            (r) => String(r.id) === String(c.regionId || c.RegionId),
          );
          return (
            region &&
            String(region.bancaId) === String(this.currentSelectedBanca.id)
          );
        });
      }

      return list;
    },
    filterSellersByAccess(sellers) {
      let list = [...(sellers || [])];

      if (this.isCobrador) {
        list = list.filter(
          (s) =>
            String(s.collectorId || s.CollectorId || "") ===
            String(this.currentUser?.id),
        );
      }

      if (!this.isAdmin) {
        list = list.filter((s) =>
          this.userResponsibleRegions.some(
            (id) => String(id) === String(s.regionId || s.RegionId),
          ),
        );
      }

      if (this.currentRegionId) {
        list = list.filter(
          (s) =>
            String(s.regionId || s.RegionId || "") ===
            String(this.currentRegionId),
        );
      } else if (this.isAdmin && this.currentSelectedBanca) {
        const bancaRegionIds = this.regions
          .filter(
            (r) =>
              String(r.bancaId) === String(this.currentSelectedBanca.id),
          )
          .map((r) => String(r.id));
        list = list.filter((s) =>
          bancaRegionIds.includes(String(s.regionId || s.RegionId || "")),
        );
      }

      return list;
    },
    async loadCollectors() {
      try {
        const res = await mainService.searchCollectors({});
        const list = res?.data || [];
        const mapped = Array.isArray(list)
          ? list.map((c) => ({
              ...c,
              name: c.name || c.username || "Cobrador",
              regionId: c.regionId || c.RegionId || null,
            }))
          : [];
        this.collectors = this.filterCollectorsByAccess(mapped).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", "pt-BR", {
            sensitivity: "base",
          }),
        );
      } catch (e) {
        this.collectors = [];
      }
    },
    async loadSellers() {
      try {
        let list = [];

        if (this.isCobrador && this.currentUser?.id) {
          const res = await mainService.getSellersByCollector(this.currentUser.id);
          list = res?.data || [];
        } else if (this.currentRegionId) {
          const res = await mainService.searchSellersByRegion(this.currentRegionId);
          list = res?.data || [];
        } else if (
          !this.isAdmin &&
          this.userResponsibleRegions.length > 0
        ) {
          const batches = await Promise.all(
            this.userResponsibleRegions.map(async (regionId) => {
              try {
                const res = await mainService.searchSellersByRegion(regionId);
                return res?.data || [];
              } catch {
                return [];
              }
            }),
          );
          const map = new Map();
          batches.flat().forEach((s) => {
            if (s?.id) map.set(String(s.id), s);
          });
          list = [...map.values()];
        } else {
          const res = await mainService.searchSellers();
          list = res?.data || [];
        }

        const mapped = Array.isArray(list)
          ? list.map((s) => ({
              ...s,
              name: s.name || s.login || "Vendedor",
              regionId: s.regionId || s.RegionId || null,
              collectorId: s.collectorId || s.CollectorId || null,
            }))
          : [];

        this.sellers = this.filterSellersByAccess(mapped).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", "pt-BR", {
            sensitivity: "base",
          }),
        );
      } catch (e) {
        this.sellers = [];
      }
    },
    buildSearchPayload(page = 1) {
      let collectorId = this.filters.collectorId || null;
      let regionId = this.currentRegionId || null;

      // Cobrador só consulta a si mesmo
      if (this.isCobrador) {
        collectorId = this.currentUser?.id || collectorId;
      }

      // Não-admin sem região no header: se tiver só uma região, usa ela
      if (
        !this.isAdmin &&
        !regionId &&
        this.userResponsibleRegions.length === 1
      ) {
        regionId = this.userResponsibleRegions[0];
      }

      // Validação: não permitir consultar cobrador fora do escopo
      if (
        collectorId &&
        !(this.collectors || []).some(
          (c) => String(c.id) === String(collectorId),
        )
      ) {
        collectorId = this.isCobrador ? this.currentUser?.id || null : null;
      }

      let sellerId = this.filters.sellerId || null;
      if (
        sellerId &&
        !(this.sellers || []).some((s) => String(s.id) === String(sellerId))
      ) {
        sellerId = null;
      }

      return {
        collectorId,
        sellerId,
        regionId: regionId || null,
        dateBegin: this.filters.dateBegin
          ? `${this.filters.dateBegin}T00:00:00`
          : null,
        dateEnd: this.filters.dateEnd
          ? `${this.filters.dateEnd}T23:59:59`
          : null,
        pageIndex: page - 1,
        pageSize: this.pageSize,
      };
    },
    applyAccessFilterToRows(rows) {
      let list = Array.isArray(rows) ? [...rows] : [];

      if (this.isCobrador) {
        list = list.filter(
          (row) =>
            String(row.collectorId || row.CollectorId || "") ===
            String(this.currentUser?.id),
        );
        return list;
      }

      if (!this.isAdmin) {
        list = list.filter((row) =>
          this.userResponsibleRegions.some(
            (id) =>
              String(id) === String(row.regionId || row.RegionId || ""),
          ),
        );
      }

      if (this.currentRegionId) {
        list = list.filter(
          (row) =>
            String(row.regionId || row.RegionId || "") ===
            String(this.currentRegionId),
        );
      }

      return list;
    },
    resetSummary() {
      this.summary = {
        totalCharges: 0,
        totalAmountReceived: 0,
        totalAmountDueBefore: 0,
        totalAmountOpenAfter: 0,
        sellersAttendedCount: 0,
        sellersWithoutChargeCount: 0,
        eligibleSellersCount: 0,
      };
    },
    async loadSummary() {
      try {
        const payload = this.buildSearchPayload(1);
        const res = await mainService.summaryCollectorCharges(payload);
        const data = res?.data?.result ?? res?.data ?? {};
        this.summary = {
          totalCharges: Number(data.totalCharges ?? data.TotalCharges ?? 0),
          totalAmountReceived: Number(
            data.totalAmountReceived ?? data.TotalAmountReceived ?? 0,
          ),
          totalAmountDueBefore: Number(
            data.totalAmountDueBefore ?? data.TotalAmountDueBefore ?? 0,
          ),
          totalAmountOpenAfter: Number(
            data.totalAmountOpenAfter ?? data.TotalAmountOpenAfter ?? 0,
          ),
          sellersAttendedCount: Number(
            data.sellersAttendedCount ?? data.SellersAttendedCount ?? 0,
          ),
          sellersWithoutChargeCount: Number(
            data.sellersWithoutChargeCount ?? data.SellersWithoutChargeCount ?? 0,
          ),
          eligibleSellersCount: Number(
            data.eligibleSellersCount ?? data.EligibleSellersCount ?? 0,
          ),
        };
      } catch (e) {
        this.resetSummary();
      }
    },
    async search(page = 1) {
      if (!this.filters.dateBegin || !this.filters.dateEnd) {
        toast.warning("Informe o período de datas");
        return;
      }
      if (this.filters.dateBegin > this.filters.dateEnd) {
        toast.warning("Data início não pode ser maior que data fim");
        return;
      }
      this.loading = true;
      this.pageIndex = page;
      try {
        const payload = this.buildSearchPayload(page);
        const [res] = await Promise.all([
          mainService.searchCollectorCharges(payload),
          this.loadSummary(),
        ]);
        const body = res?.data;
        let rows = body?.result || body || [];
        if (!Array.isArray(rows)) rows = [];
        this.rows = this.applyAccessFilterToRows(rows);
        this.totalRows = body?.totalRows ?? this.rows.length;
        this.totalPages = body?.totalPages ?? 1;
      } catch (e) {
        this.rows = [];
        this.resetSummary();
        toast.error(handleErrorMessage(e));
      } finally {
        this.loading = false;
      }
    },

    loadVisibleColumns() {
      try {
        const saved = localStorage.getItem(COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.visibleColumns = {
          ...DEFAULT_VISIBLE_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          chargeDateTime: true,
        };
      } catch {
        this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      }
    },
    saveVisibleColumns() {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(this.visibleColumns));
    },
    isColumnVisible(key) {
      if (key === "chargeDateTime") return true;
      return this.visibleColumns[key] !== false;
    },
    toggleColumn(key) {
      if (key === "chargeDateTime") return;
      this.visibleColumns = {
        ...this.visibleColumns,
        [key]: !this.isColumnVisible(key),
      };
      this.saveVisibleColumns();
    },
    resetVisibleColumns() {
      this.visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
      this.saveVisibleColumns();
    },
    handleColumnsMenuOutsideClick(event) {
      if (this.showColumnsMenu) {
        const dropdown = this.$refs.columnsDropdown;
        if (dropdown && !dropdown.contains(event.target)) {
          this.showColumnsMenu = false;
        }
      }
      if (this.showSellersExportMenu) {
        const dropdown = this.$refs.sellersExportDropdown;
        if (dropdown && !dropdown.contains(event.target)) {
          this.showSellersExportMenu = false;
        }
      }
    },
    toggleSellersExportMenu() {
      this.showSellersExportMenu = !this.showSellersExportMenu;
      if (this.showSellersExportMenu) this.showColumnsMenu = false;
    },

    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (!Number.isNaN(saved) && saved >= TABLE_ZOOM_MIN && saved <= TABLE_ZOOM_MAX) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },
    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },
    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },
    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },
    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },

    getVisibleExportColumns() {
      return this.toggleableColumns.filter((col) => this.isColumnVisible(col.key));
    },
    buildExportRow(row, formatted = false) {
      const values = {
        chargeDateTime: this.formatDateTime(row.chargeDateTime),
        collectorName: row.collectorName || "—",
        sellerName: row.sellerName || "—",
        sellerPoint: row.sellerPoint || "—",
        regionName: row.regionName || "—",
        amountDueBefore: formatted
          ? this.formatMoney(row.amountDueBefore)
          : Number(row.amountDueBefore || 0),
        amountReceived: formatted
          ? this.formatMoney(row.amountReceived)
          : Number(row.amountReceived || 0),
        amountOpenAfter: formatted
          ? this.formatMoney(row.amountOpenAfter)
          : Number(row.amountOpenAfter || 0),
        description: row.description || "—",
      };
      return this.getVisibleExportColumns().map((col) => values[col.key]);
    },

    buildSummaryPrintHtml() {
      const s = this.summary || {};
      return `
        <div class="summary-grid">
          <div class="summary-card">
            <div class="summary-label">Cobranças</div>
            <div class="summary-value">${Number(s.totalCharges || 0)}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Valor recebido</div>
            <div class="summary-value">${this.formatMoney(s.totalAmountReceived)}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Saldo final (soma)</div>
            <div class="summary-value">${this.formatMoney(s.totalAmountOpenAfter)}</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Vendedores atendidos</div>
            <div class="summary-value">${Number(s.sellersAttendedCount || 0)}</div>
            <div class="summary-hint">de ${Number(s.eligibleSellersCount || 0)} elegíveis</div>
          </div>
          <div class="summary-card">
            <div class="summary-label">Sem cobrança</div>
            <div class="summary-value">${Number(s.sellersWithoutChargeCount || 0)}</div>
            <div class="summary-hint">no período filtrado</div>
          </div>
        </div>
      `;
    },

    printReport() {
      if (!this.rows?.length) {
        toast.warning("Não há dados para imprimir");
        return;
      }
      const cols = this.getVisibleExportColumns();
      const headerRow = cols.map((c) => `<th>${c.label}</th>`).join("");
      const bodyRows = this.rows
        .map((row) => {
          const cells = this.buildExportRow(row, true)
            .map((v) => `<td>${v ?? ""}</td>`)
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Registros de Cobrança</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            h1 { text-align: center; margin-bottom: 8px; }
            .info { text-align: center; color: #666; margin-bottom: 16px; font-size: 13px; }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              gap: 10px;
              margin: 0 0 18px 0;
            }
            .summary-card {
              border: 1px solid #ddd;
              border-radius: 8px;
              padding: 10px 12px;
              background: #f8fafc;
            }
            .summary-label {
              font-size: 11px;
              color: #64748b;
              font-weight: 600;
              margin-bottom: 4px;
            }
            .summary-value {
              font-size: 15px;
              font-weight: 700;
              color: #111;
            }
            .summary-hint {
              font-size: 10px;
              color: #94a3b8;
              margin-top: 2px;
            }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; white-space: nowrap; }
            th { background: #e5e5e5; }
            tr:nth-child(even) { background: #f9f9f9; }
            @media print {
              .summary-grid { grid-template-columns: repeat(5, 1fr); }
              .summary-card { break-inside: avoid; }
            }
            @media (max-width: 900px) {
              .summary-grid { grid-template-columns: repeat(2, 1fr); }
            }
          </style>
        </head>
        <body>
          <h1>Registros de Cobrança</h1>
          <div class="info">
            Período: ${this.periodDisplayText()}
            · ${this.totalRows} registro(s)
          </div>
          ${this.buildSummaryPrintHtml()}
          <table>
            <thead><tr>${headerRow}</tr></thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    exportToPDF() {
      if (!this.rows?.length) {
        toast.warning("Não há dados para exportar");
        return;
      }
      try {
        const doc = new jsPDF("landscape", "mm", "a4");
        const cols = this.getVisibleExportColumns();
        const s = this.summary || {};
        doc.setFontSize(14);
        doc.text("Registros de Cobrança", 14, 14);
        doc.setFontSize(10);
        doc.text(
          `Período: ${this.periodDisplayText()}`,
          14,
          20,
        );
        autoTable(doc, {
          startY: 24,
          head: [["Indicador", "Valor"]],
          body: [
            ["Cobranças", String(Number(s.totalCharges || 0))],
            ["Valor recebido", this.formatMoney(s.totalAmountReceived)],
            ["Saldo final (soma)", this.formatMoney(s.totalAmountOpenAfter)],
            [
              "Vendedores atendidos",
              `${Number(s.sellersAttendedCount || 0)} de ${Number(s.eligibleSellersCount || 0)}`,
            ],
            ["Sem cobrança", String(Number(s.sellersWithoutChargeCount || 0))],
          ],
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [37, 99, 235] },
          columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 80 } },
        });
        const summaryEndY = doc.lastAutoTable?.finalY
          ? doc.lastAutoTable.finalY + 6
          : 55;
        autoTable(doc, {
          startY: summaryEndY,
          head: [cols.map((c) => c.label)],
          body: this.rows.map((row) => this.buildExportRow(row, true)),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [37, 99, 235] },
        });
        doc.save(`cobrancas_${this.filters.dateBegin || "inicio"}_${this.filters.dateEnd || "fim"}.pdf`);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error(error);
        toast.error("Erro ao exportar PDF");
      }
    },

    exportToExcel() {
      if (!this.rows?.length) {
        toast.warning("Não há dados para exportar");
        return;
      }
      try {
        const cols = this.getVisibleExportColumns();
        const headers = cols.map((c) => c.label);
        const data = this.rows.map((row) => this.buildExportRow(row, false));
        const s = this.summary || {};
        const wb = XLSX.utils.book_new();
        const sheetData = [
          ["Registros de Cobrança"],
          [`Período: ${this.periodDisplayText()}`],
          [],
          ["Resumo"],
          ["Cobranças", Number(s.totalCharges || 0)],
          ["Valor recebido", Number(s.totalAmountReceived || 0)],
          ["Saldo final (soma)", Number(s.totalAmountOpenAfter || 0)],
          ["Vendedores atendidos", Number(s.sellersAttendedCount || 0)],
          ["Vendedores elegíveis", Number(s.eligibleSellersCount || 0)],
          ["Sem cobrança", Number(s.sellersWithoutChargeCount || 0)],
          [],
          headers,
          ...data,
        ];
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(wb, ws, "Cobranças");
        XLSX.writeFile(
          wb,
          `cobrancas_${this.filters.dateBegin || "inicio"}_${this.filters.dateEnd || "fim"}.xlsx`,
        );
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error(error);
        toast.error("Erro ao exportar Excel");
      }
    },

    async fetchSellersCoverage(hasChargeInPeriod) {
      const payload = {
        ...this.buildSearchPayload(1),
        hasChargeInPeriod: !!hasChargeInPeriod,
      };
      const res = await mainService.sellersCoverageCollectorCharges(payload);
      const data = res?.data?.result ?? res?.data ?? [];
      return Array.isArray(data) ? data : [];
    },
    buildSellersCoverageExportRows(items, formatted = false) {
      return (items || []).map((row) => {
        const lastDue = row.lastAmountDueBefore ?? row.LastAmountDueBefore;
        const lastRecv = row.lastAmountReceived ?? row.LastAmountReceived;
        const lastOpen = row.lastAmountOpenAfter ?? row.LastAmountOpenAfter;
        const lastDt = row.lastChargeDateTime ?? row.LastChargeDateTime;
        return [
          row.sellerName ?? row.SellerName ?? "—",
          row.sellerPoint ?? row.SellerPoint ?? "—",
          row.sellerLogin ?? row.SellerLogin ?? "—",
          row.regionName ?? row.RegionName ?? "—",
          row.collectorName ?? row.CollectorName ?? "—",
          Number(row.chargesInPeriodCount ?? row.ChargesInPeriodCount ?? 0),
          lastDt ? this.formatDateTime(lastDt) : "—",
          lastDue == null || lastDue === ""
            ? "—"
            : formatted
              ? this.formatMoney(lastDue)
              : Number(lastDue),
          lastRecv == null || lastRecv === ""
            ? "—"
            : formatted
              ? this.formatMoney(lastRecv)
              : Number(lastRecv),
          lastOpen == null || lastOpen === ""
            ? "—"
            : formatted
              ? this.formatMoney(lastOpen)
              : Number(lastOpen),
          row.lastCollectorName ?? row.LastCollectorName ?? "—",
          row.lastDescription ?? row.LastDescription ?? "—",
        ];
      });
    },
    async exportSellersCoverage(hasChargeInPeriod, format = "excel") {
      if (this.exportingSellers) return;
      if (!this.filters.dateBegin || !this.filters.dateEnd) {
        toast.warning("Informe o período antes de exportar");
        return;
      }
      this.showSellersExportMenu = false;
      this.exportingSellers = true;
      const label = hasChargeInPeriod ? "com_cobranca" : "sem_cobranca";
      const title = hasChargeInPeriod
        ? "Vendedores com cobrança no período"
        : "Vendedores sem cobrança no período";
      try {
        const items = await this.fetchSellersCoverage(hasChargeInPeriod);
        if (!items.length) {
          toast.warning(
            hasChargeInPeriod
              ? "Nenhum vendedor com cobrança no período"
              : "Nenhum vendedor sem cobrança no período",
          );
          return;
        }
        const headers = [
          "Vendedor",
          "Ponto",
          "Login",
          "Região",
          "Cobrador",
          "Cobranças no período",
          "Última cobrança",
          "Saldo anterior (últ.)",
          "Valor (últ.)",
          "Saldo final (últ.)",
          "Cobrador (últ.)",
          "Descrição (últ.)",
        ];
        if (format === "pdf") {
          const doc = new jsPDF("landscape", "mm", "a4");
          doc.setFontSize(13);
          doc.text(title, 14, 14);
          doc.setFontSize(9);
          doc.text(
            `Período: ${this.periodDisplayText()} · ${items.length} vendedor(es)`,
            14,
            20,
          );
          autoTable(doc, {
            startY: 24,
            head: [headers],
            body: this.buildSellersCoverageExportRows(items, true),
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: [37, 99, 235] },
          });
          doc.save(
            `vendedores_${label}_${this.filters.dateBegin || "inicio"}_${this.filters.dateEnd || "fim"}.pdf`,
          );
          toast.success("PDF de vendedores exportado!");
        } else {
          const wb = XLSX.utils.book_new();
          const sheetData = [
            [title],
            [
              `Período: ${this.periodDisplayText()}`,
            ],
            [`Total: ${items.length} vendedor(es)`],
            [],
            headers,
            ...this.buildSellersCoverageExportRows(items, false),
          ];
          const ws = XLSX.utils.aoa_to_sheet(sheetData);
          XLSX.utils.book_append_sheet(wb, ws, "Vendedores");
          XLSX.writeFile(
            wb,
            `vendedores_${label}_${this.filters.dateBegin || "inicio"}_${this.filters.dateEnd || "fim"}.xlsx`,
          );
          toast.success("Excel de vendedores exportado!");
        }
      } catch (error) {
        console.error(error);
        handleErrorMessage(error);
        toast.error("Erro ao exportar vendedores");
      } finally {
        this.exportingSellers = false;
      }
    },
  },
};
</script>

<style scoped>
/* Permite o dropdown sair do card (global usa overflow:hidden) */
.results-container :deep(.filters-card),
.filters-card,
.results-container :deep(.results-card),
.results-card {
  overflow: visible !important;
  position: relative;
}

.filters-card {
  z-index: 40;
}

.results-card {
  z-index: 30;
}

.results-card > .table-container {
  position: relative;
  z-index: 1;
}

.filters-content {
  overflow: visible !important;
  position: relative;
  z-index: 21;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
  overflow: visible !important;
  position: relative;
  z-index: 22;
}

.filters-grid .input-group {
  overflow: visible !important;
  position: relative;
  z-index: 1;
}

.filters-grid .input-group:focus-within {
  z-index: 50;
}

.filters-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}

.filters-actions .btn-secondary {
  background-color: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.filters-actions .btn-secondary:hover {
  background-color: var(--text-muted);
  color: white;
}

.charges-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.25rem;
}

.charges-summary-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  border: 1px solid var(--border-color, #e2e8f0);
  background: var(--card-background, #fff);
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.08));
  min-width: 0;
}

.summary-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-size: 1rem;
}

.card-charges .summary-icon { background: #2563eb; }
.card-received .summary-icon { background: #16a34a; }
.card-open .summary-icon { background: #ea580c; }
.card-attended .summary-icon { background: #0891b2; }
.card-pending .summary-icon { background: #64748b; }

.charges-summary-card.is-clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}
.charges-summary-card.is-clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  border-color: #94a3b8;
}

.summary-body {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.summary-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-secondary, #6b7280);
  white-space: nowrap;
}

.summary-value {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.summary-hint {
  font-size: 0.72rem;
  color: var(--text-muted, #9ca3af);
  white-space: nowrap;
}

.searchable-select-wrapper {
  position: relative;
  width: 100%;
  z-index: 2;
}

.searchable-select {
  position: relative;
}

.searchable-select-input {
  width: 100%;
  padding-right: 2rem !important;
}

.searchable-select-arrow {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--text-secondary, #6b7280);
  font-size: 0.7rem;
}

.searchable-select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 9999;
  max-height: 260px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
}

[data-theme="dark"] .searchable-select-dropdown {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.searchable-select-option {
  padding: 0.65rem 0.85rem;
  cursor: pointer;
  color: var(--text-primary);
  font-size: 0.9rem;
  white-space: nowrap;
}

.searchable-select-option:hover {
  background: rgba(37, 99, 235, 0.08);
}

.searchable-select-option.is-selected {
  background: rgba(37, 99, 235, 0.14);
  font-weight: 600;
}

.searchable-select-option.no-results {
  cursor: default;
  color: var(--text-secondary, #6b7280);
  font-style: italic;
}

.searchable-select-option.no-results:hover {
  background: transparent;
}

.charges-table-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  position: relative;
  z-index: 40;
  overflow: visible !important;
}

.charges-table-header .card-title {
  margin: 0;
  flex: 0 1 auto;
}

.card-actions {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 50;
  overflow: visible !important;
  flex: 1 1 auto;
  justify-content: flex-end;
  min-width: 0;
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
  flex: 0 0 auto;
  margin-right: 0.25rem;
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  flex-shrink: 0;
}

.btn-zoom:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

/* Sobrescreve .btn-action global (32x32) que quebrava os botões de texto */
.card-actions .btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 0.9rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  color: white;
  white-space: nowrap;
  flex: 0 0 auto;
  box-sizing: border-box;
  overflow: visible;
}

.card-actions .btn-action.btn-print,
.card-actions .btn-action.btn-pdf,
.card-actions .btn-action.btn-excel,
.card-actions .btn-action.btn-sellers-export,
.card-actions .btn-action.btn-columns {
  width: auto !important;
}

.btn-print { background-color: #6b7280; }
.btn-print:hover { background-color: #4b5563; }
.btn-pdf { background-color: #dc2626; }
.btn-pdf:hover { background-color: #b91c1c; }
.btn-excel { background-color: #16a34a; }
.btn-excel:hover { background-color: #15803d; }
.btn-sellers-export { background-color: #0d9488; }
.btn-sellers-export:hover { background-color: #0f766e; }
.btn-columns { background-color: #2563eb; }
.btn-columns:hover { background-color: #1d4ed8; }

.sellers-export-menu {
  min-width: 240px;
}

.sellers-export-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.55rem 0.65rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #111);
}
.sellers-export-item:hover {
  background: rgba(13, 148, 136, 0.12);
}
.sellers-export-item i {
  width: 1rem;
  text-align: center;
}

.columns-dropdown {
  position: relative;
  z-index: 60;
  flex: 0 0 auto;
  overflow: visible !important;
}

.columns-caret {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.columns-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 9999;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 0.5rem;
}

[data-theme="dark"] .columns-menu {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.columns-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
}

.columns-reset {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
}

.columns-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  font-size: 0.85rem;
  cursor: pointer;
}

.columns-menu-item.locked {
  opacity: 0.65;
  cursor: default;
}

.charges-table th,
.charges-table td {
  white-space: nowrap !important;
}

.nowrap-cell {
  white-space: nowrap !important;
  flex-wrap: nowrap !important;
}

.currency-value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  padding: 0.5rem;
  border: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
  border-color: var(--accent-primary);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: var(--text-primary);
  min-width: 60px;
  text-align: center;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
}

@media (max-width: 768px) {
  .pagination-footer {
    flex-direction: column;
    gap: 1rem;
  }

  .filters-actions {
    flex-direction: column;
  }

  .filters-actions .btn-primary,
  .filters-actions .btn-secondary {
    width: 100%;
  }
}
</style>
